import axios from '@/lib/axios'
import { useInfiniteQuery, useQuery } from '@tanstack/vue-query'
import { type MaybeRef, get } from '@vueuse/core'
import { computed, ref } from 'vue'

import type { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'

import type { HotelSearchDefaultLocation } from '../interfaces/hotelSearchSettings'

export interface HotelBranch {
	id: number
	name: string
	address: string | null
	latitude: number
	longitude: number
	radiusKm: number | null
	effectiveRadiusKm?: number
	hotelPreferenceMode?: 'prioritize' | 'restrict' | null
	/** La sede principal de la empresa: BookingV2 la manda primero. */
	isDefault?: boolean
}

export interface BranchSuggestion {
	id: string
	name: string
	/** Nulo en la dirección de la empresa: no es una sede y se busca sin `companyBranchId`. */
	branchId: number | null
	branchName: string
	latitude: number
	longitude: number
	radiusKm: number | null
	effectiveRadiusKm?: number
	hotelPreferenceMode?: 'prioritize' | 'restrict' | null
	isDefault?: boolean
	isCompanyAddress?: boolean
	/** La dirección, para decir dónde se busca (la dirección de la empresa). */
	address?: string | null
}

export const isBranchSuggestion = (value: unknown): value is BranchSuggestion =>
	typeof value === 'object' && value !== null && 'branchId' in value

export const toBranchSuggestion = (branch: HotelBranch): BranchSuggestion => ({
	id: `branch-${branch.id}`,
	name: branch.address ? `${branch.name} · ${branch.address}` : branch.name,
	branchId: branch.id,
	branchName: branch.name,
	latitude: branch.latitude,
	longitude: branch.longitude,
	radiusKm: branch.radiusKm,
	effectiveRadiusKm: branch.effectiveRadiusKm,
	hotelPreferenceMode: branch.hotelPreferenceMode,
	isDefault: branch.isDefault,
})

/** La ubicación con la que abre el buscador, como la sede elegida del formulario. */
export const fromDefaultLocation = (
	location: HotelSearchDefaultLocation
): BranchSuggestion => ({
	id:
		location.type === 'branch' ?
			`branch-${location.companyBranchId}`
		:	'company-address',
	name:
		location.address ? `${location.name} · ${location.address}` : location.name,
	branchId: location.type === 'branch' ? location.companyBranchId : null,
	branchName: location.name,
	latitude: location.latitude,
	longitude: location.longitude,
	radiusKm: location.radiusKm,
	effectiveRadiusKm: location.effectiveRadiusKm,
	hotelPreferenceMode: location.hotelPreferenceMode,
	isDefault: true,
	isCompanyAddress: location.type === 'company',
	address: location.address,
})

interface BranchPage {
	data: HotelBranch[]
	meta: { currentPage: number; lastPage: number; total: number }
}

/** Tres accesos rápidos y un catálogo buscable, consultado por páginas de diez. */
const useHotelBranches = (
	memberId: MaybeRef<number | undefined>,
	isInternal: MaybeRef<boolean>,
	users: MaybeRef<number[]> = []
) => {
	const enabled = computed(() => !get(isInternal) || !!get(memberId))
	const query = ref('')
	const scope = computed(() => ({ memberId: get(memberId), users: get(users) }))
	const request = async (
		params: Record<string, unknown>
	): Promise<BranchPage> =>
		(
			await axios.get<BranchPage>('/api/hotels/branches', {
				params: { ...scope.value, ...params },
				errorHandling: false,
			} as CustomAxiosRequestConfig)
		).data

	const quick = useQuery(
		['hotelBranchesQuick', scope],
		() => request({ perPage: 3, page: 1 }),
		{
			enabled,
			retry: false,
			staleTime: 60_000,
		}
	)
	const catalog = useInfiniteQuery(
		['hotelBranches', scope, query],
		({ pageParam = 1 }) =>
			request({ q: query.value, perPage: 10, page: pageParam }),
		{
			enabled,
			retry: false,
			staleTime: 60_000,
			getNextPageParam: (last) =>
				last.meta.currentPage < last.meta.lastPage ?
					last.meta.currentPage + 1
				:	undefined,
		}
	)

	const suggestions = computed(
		() =>
			catalog.data.value?.pages.flatMap((page) =>
				page.data.map(toBranchSuggestion)
			) ?? []
	)
	const findBranch = async (
		id: number
	): Promise<BranchSuggestion | undefined> => {
		const cached = suggestions.value.find((branch) => branch.branchId === id)
		if (cached) return cached
		const { data } = await axios.get<{ data: HotelBranch[] }>(
			'/api/hotels/branches',
			{
				params: { ...scope.value, branchId: id },
				errorHandling: false,
			} as CustomAxiosRequestConfig
		)
		return data.data[0] ? toBranchSuggestion(data.data[0]) : undefined
	}

	return {
		quickBranches: computed(
			() => quick.data.value?.data.map(toBranchSuggestion) ?? []
		),
		total: computed(() => quick.data.value?.meta.total ?? 0),
		suggestions,
		query,
		findBranch,
		isFetching: catalog.isFetching,
		isError: catalog.isError,
		refetch: catalog.refetch,
		hasNextPage: catalog.hasNextPage,
		fetchNextPage: catalog.fetchNextPage,
		isFetchingNextPage: catalog.isFetchingNextPage,
	}
}

export default useHotelBranches
